class ScrollManager {
  constructor(options = {}) {
    this.options = {
      container: '#scroll-container',
      sections: '.scroll-section',
      navigation: '.scroll-nav-dot',
      upButton: '#scroll-up',
      downButton: '#scroll-down',
      progressBar: '.progress-fill',
      sectionCounter: '#current-section',
      transitionDuration: 1000,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
      parallaxIntensity: 0.5,
      horizontalSectionIndex: 1, // <-- la 2ª section (0-based)
      horizontalListSelector: '.categories-section ul.row', // <-- la UL target dentro la 2ª section
      horizontalStep: 'auto', // 'auto' = larghezza card; oppure numero in px
      horizontalSnap: true,
      ...options,
    };

    this.currentSection = 0;
    this.totalSections = 0;
    this.isTransitioning = false;

    // REST.ARÍA — controllo scroll a scatti
    this.lastScrollTime = 0;
    this.scrollThrottle = 100;

    this.wheelGestureActive = false;
    this.wheelGestureTimer = null;
    this.wheelCooldownUntil = 0;

    this.init();
  }

  init() {
    this.bindElements();
    this.setupSections();
    this.attachEventListeners();
    this.updateUI();
    this.resizePersonalized();
    window.addEventListener('resize', () => this.setupSections());
  }

  bindElements() {
    this.container = document.querySelector(this.options.container);
    this.sections = document.querySelectorAll(this.options.sections);
    this.navDots = document.querySelectorAll(this.options.navigation);
    this.upButton = document.querySelector(this.options.upButton);
    this.downButton = document.querySelector(this.options.downButton);
    this.progressBar = document.querySelector(this.options.progressBar);
    this.sectionCounter = document.querySelector(this.options.sectionCounter);
    this.footer = document.getElementsByTagName('footer');
    this.body = document.body;
    // Bind parallax elements
    this.parallaxElements = document.querySelectorAll('.hero-parallax-img');

    if (!this.container || !this.sections.length) {
      console.error('ScrollManager: Required elements not found');
      return;
    }

    this.totalSections = this.sections.length;
  }

  setupSections() {
    if (!this.container) return;

    const viewportHeight = window.innerHeight;
    const top = Math.max(0, this.container.getBoundingClientRect().top);

    const height = Math.max(240, Math.round(viewportHeight - top));

    this.sectionHeight = height;
    this.container.style.height = `${height}px`;
    this.container.style.overflow = 'hidden';
    this.container.style.position = 'relative';

    this.sections.forEach((section, index) => {
      section.style.position = 'absolute';
      section.style.top = '0';
      section.style.left = '0';
      section.style.width = '100%';
      section.style.height = `${height}px`;
      section.style.minHeight = '0';

      section.style.transform = `translate3d(0, ${(index - this.currentSection) * height}px, 0)`;

      section.style.transition = `transform ${this.options.transitionDuration}ms ${this.options.easing}`;
    });

    this.resizePersonalized();
  }

  attachEventListeners() {
    // Wheel navigation
    this.body.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

    // Touch navigation
    let startY = 0;
    let previousY = 0;
    let consumedTouch = false;
    this.body.addEventListener(
      'touchstart',
      (e) => {
        startY = previousY = e.touches[0].clientY;
        consumedTouch = false;
      },
      { passive: true },
    );

    // BLOCCO pull-to-refresh: preveniamo il default quando si tira verso il basso
    this._onTouchMove = (e) => {
      const currentY = e.touches[0].clientY;
      if (e.target.closest?.('.modal, input, textarea, select')) return;
      if (this.scrollPersonalized(previousY - currentY)) {
        consumedTouch = true;
        previousY = currentY;
        e.preventDefault();
        return;
      }
      previousY = currentY;
      const deltaY = currentY - startY; // > 0 => swipe down
      // Condizione: quando il tuo ScrollManager è attivo gestisci tu lo scroll ⇒ blocca il default
      if (deltaY > 0) {
        e.preventDefault();
      }
    };
    this.body.addEventListener('touchmove', this._onTouchMove, { passive: false });

    this.body.addEventListener(
      'touchend',
      (e) => {
        if (this.isTransitioning || consumedTouch || e.target.closest?.('.modal, input, textarea, select')) return;

        const endY = e.changedTouches[0].clientY;
        const deltaY = startY - endY;

        if (Math.abs(deltaY) > 45) {
          if (deltaY > 0) {
            this.navigateToNext();
          } else {
            this.navigateToPrev();
          }
        }
      },
      { passive: true },
    );

    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeyDown.bind(this));

    // Navigation dots
    this.navDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.navigateToSection(index));
    });

    // Up/Down buttons
    this.upButton?.addEventListener('click', () => this.navigateToPrev());
    this.downButton?.addEventListener('click', () => this.navigateToNext());

    // Parallax scroll effect
    window.addEventListener('scroll', this.handleParallax.bind(this), { passive: true });

    // Resize handler
    // window.addEventListener('resize', this.handleResize.bind(this));

    // Prevent default scroll behavior
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('resta-shop-desktop-active');
  }

  resizePersonalized() {
    const section = this.container?.querySelector('#personalized-section');
    if (!section) return;

    section.style.setProperty('--resta-personalized-height', `${this.sectionHeight || this.container.clientHeight}px`);
  }

  scrollPersonalized(delta) {
    const section = this.sections[this.currentSection];
    if (!section || section.id !== 'personalized-section') return false;
    const max = Math.max(0, section.scrollHeight - section.clientHeight);
    if ((delta > 0 && section.scrollTop < max - 1) || (delta < 0 && section.scrollTop > 1)) {
      section.scrollTop = Math.max(0, Math.min(max, section.scrollTop + delta));
      return true;
    }
    return false;
  }

  handleWheel(e) {
    if (e.target.closest?.('.modal, input, textarea, select') || e.ctrlKey) return;

    e.preventDefault();

    const delta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);

    // Mantiene lo scroll interno della sezione personalizzazione.
    if (this.scrollPersonalized(delta)) return;

    const now = performance.now();

    // Un gesto termina quando non arrivano nuovi
    // eventi della rotellina per 320 millisecondi.
    clearTimeout(this.wheelGestureTimer);

    this.wheelGestureTimer = setTimeout(() => {
      this.wheelGestureActive = false;
    }, 320);

    // Impedisce altri passaggi durante lo stesso gesto
    // e durante il completamento dell'animazione.
    if (this.wheelGestureActive || this.isTransitioning || now < this.wheelCooldownUntil) return;

    // Ignora movimenti minimi accidentali.
    if (Math.abs(delta) < 3) return;

    // Consente un solo movimento per gesto.
    this.wheelGestureActive = true;

    // Attende il completamento dell'animazione,
    // aggiungendo un piccolo margine di sicurezza.
    this.wheelCooldownUntil = now + this.options.transitionDuration + 250;

    if (delta > 0) {
      this.navigateToNext();
    } else {
      this.navigateToPrev();
    }
  }

  handleKeyDown(e) {
    if (this.isTransitioning || e.target.closest?.('input, textarea, select, button, a, [contenteditable], .modal'))
      return;
    const direction = ['ArrowDown', 'PageDown', ' '].includes(e.key)
      ? e.shiftKey
        ? -1
        : 1
      : ['ArrowUp', 'PageUp'].includes(e.key)
        ? -1
        : 0;
    if (
      direction &&
      this.scrollPersonalized(direction * (e.key.startsWith('Arrow') ? 60 : window.innerHeight * 0.75))
    ) {
      e.preventDefault();
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
      case 'PageUp':
        e.preventDefault();
        this.navigateToPrev();
        break;
      case 'ArrowDown':
      case 'PageDown':
      case ' ':
        e.preventDefault();
        this.navigateToNext();
        break;
      case 'Home':
        e.preventDefault();
        this.navigateToSection(0);
        break;
      case 'End':
        e.preventDefault();
        this.navigateToSection(this.totalSections - 1);
        break;
    }
  }

  handleParallax() {
    // Apply parallax effect to hero image
    this.parallaxElements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const scrolled = window.pageYOffset;
      const parallax = scrolled * this.options.parallaxIntensity;
      element.style.transform = `translateY(${parallax}px)`;
    });
  }

  handleResize() {
    // Recalculate section positions on resize
    this.setupSections();
  }

  navigateToNext() {
    // Se siamo nella section orizzontale e possiamo scorrere a destra, gestisci qui e fermati
    if (this.currentSection < this.totalSections - 1) {
      this.navigateToSection(this.currentSection + 1);
    }
  }

  navigateToPrev() {
    // Se siamo nella section orizzontale e possiamo scorrere a sinistra, gestisci qui e fermati
    if (this.currentSection === this.options.horizontalSectionIndex) {
      if (this.tryHorizontalScroll(-1)) return;
    }
    if (this.currentSection > 0) {
      this.navigateToSection(this.currentSection - 1);
    }
  }

  // Trova la UL orizzontale dentro alla 2ª section
  getHorizontalListEl() {
    const sec = this.sections?.[this.options.horizontalSectionIndex];
    if (!sec) return null;
    return sec.querySelector(this.options.horizontalListSelector);
  }

  // Calcola lo "step" di scroll: larghezza card + gap (se 'auto')
  _getHorizontalStep(listEl) {
    if (this.options.horizontalStep !== 'auto') return this.options.horizontalStep;

    // prova a prendere una card .col o li
    const item = listEl.querySelector('.col, li');
    const style = getComputedStyle(listEl);
    const gap = parseFloat(style.columnGap || style.gap || 0) || 0;
    if (item) return item.getBoundingClientRect().width + gap;
    // fallback: quasi una viewport
    return Math.max(300, Math.floor(listEl.clientWidth * 0.9));
  }

  // Verifica se siamo agli estremi
  _isAtStart(listEl) {
    return listEl.scrollLeft <= 0;
  }
  _isAtEnd(listEl) {
    return listEl.scrollLeft + listEl.clientWidth >= listEl.scrollWidth - 1;
  }

  /**
   * Prova a scorrere orizzontalmente nella 2ª section.
   * @param {+1|-1} dir  +1=destra, -1=sinistra
   * @returns {boolean}  true se ha gestito lo scroll orizzontale; false se deve passare al verticale
   */
  tryHorizontalScroll(dir) {
    const listEl = this.getHorizontalListEl();
    if (!listEl) return false;

    // evita conflitti con la transizione verticale in corso
    if (this.isTransitioning) return true;

    // se la lista non overflowa, lascia passare al verticale
    const maxLeft = Math.max(0, listEl.scrollWidth - listEl.clientWidth);
    if (maxLeft <= 0) return false;

    // ai bordi lascia passare la navigazione verticale
    if (dir > 0 && this._isAtEnd(listEl)) return false;
    if (dir < 0 && this._isAtStart(listEl)) return false;

    // scatto direzionale di 1 card
    const step = this._getHorizontalStep(listEl);
    const approxIndex = Math.round(listEl.scrollLeft / step);
    // baseIndex dipende dalla direzione
    const baseIndex =
      dir > 0
        ? Math.floor(approxIndex) // andando a destra, parti dall'indice "inferiore"
        : Math.ceil(approxIndex); // andando a sinistra, parti dall'indice "superiore"

    let targetIndex = baseIndex + (dir > 0 ? 1 : -1);
    // clamp ai limiti
    let maxIndex = 0;
    if (matchMedia('(max-width: 768px)').matches) {
      maxIndex = Math.floor(5);
    } else {
      maxIndex = Math.floor(maxLeft / step);
    }

    targetIndex = Math.max(0, Math.min(targetIndex, maxIndex));

    const targetLeft = Math.min(maxLeft, targetIndex * step);

    // smooth scroll orizzontale verso lo snap scelto
    listEl.scrollTo({ left: targetLeft, behavior: 'smooth' });

    return true; // evento gestito (non cambiare section)
  }
  navigateToSectionFooter(index) {
    this.navigateToSection(index);
  }

  navigateToSection(index) {
    if (index < 0 || index >= this.totalSections || index === this.currentSection || this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentSection = index;

    const destination = this.sections[index];

    if (destination?.id === 'personalized-section') {
      destination.scrollTop = 0;
    }

    const height = this.sectionHeight || this.container.clientHeight;

    this.sections.forEach((section, i) => {
      section.style.transform = `translate3d(0, ${(i - index) * height}px, 0)`;
    });

    this.updateUI();
    this.addTransitionEffect();

    setTimeout(() => {
      this.isTransitioning = false;
    }, this.options.transitionDuration);

    this.triggerSectionAnimations();
  }

  addTransitionEffect() {
    // Create subtle overlay during transition
    const overlay = document.createElement('div');
    overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.1);
            backdrop-filter: blur(2px);
            z-index: 9998;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
        `;

    document.body.appendChild(overlay);

    // Fade in
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });

    // Fade out and remove
    setTimeout(() => {
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 300);
    }, 200);
  }

  triggerSectionAnimations() {
    // Add animation classes based on current section
    const currentSectionEl = this.sections[this.currentSection];
    if (!currentSectionEl) return;

    // Remove animation classes from all sections first
    this.sections.forEach((section) => {
      section.classList.remove('section-active', 'animate-in');
    });

    // Add to current section
    setTimeout(() => {
      currentSectionEl.classList.add('section-active', 'animate-in');

      // Trigger specific animations based on section
      this.triggerSectionSpecificAnimations(this.currentSection);
    }, 100);
  }

  triggerSectionSpecificAnimations(sectionIndex) {
    const section = this.sections[sectionIndex];
    if (!section) return;

    // Animate elements within the section with staggered delays
    const animatedElements = section.querySelectorAll(
      '.category-card, .feature-item, .showcase-img-container, .personalized-content > *',
    );

    animatedElements.forEach((element, index) => {
      element.style.animationDelay = `${index * 0.1}s`;
      element.classList.add('fade-in-up');
    });

    // Section-specific triggers
    switch (sectionIndex) {
      case 0: // Hero
        this.animateHeroElements();
        break;
      case 1: // Categories
        this.animateCategoryElements();
        break;
      case 2: // Showcase
        this.animateShowcaseElements();
        break;
      case 3: // Personalized
        this.animatePersonalizedElements();
        break;
    }
  }

  animateHeroElements() {
    const heroTitle = document.querySelector('.hero-main-title');
    const heroSubtitle = document.querySelector('.hero-subtitle');
    const playButton = document.querySelector('.hero-play-button');
    const stats = document.querySelector('.hero-stats');

    if (heroTitle) heroTitle.style.animationDelay = '0.2s';
    if (heroSubtitle) heroSubtitle.style.animationDelay = '0.8s';
    if (playButton) playButton.style.animationDelay = '1.2s';
    if (stats) stats.style.animationDelay = '1.8s';
  }

  animateCategoryElements() {
    const categoryCards = document.querySelectorAll('.category-card');
    categoryCards.forEach((card, index) => {
      setTimeout(() => {
        card.classList.add('animate-in');
      }, index * 100);
    });
  }

  animateShowcaseElements() {
    const showcaseImages = document.querySelectorAll('.showcase-img-container');
    const showcaseStats = document.querySelectorAll('.showcase-section .col-md-4');

    showcaseImages.forEach((img, index) => {
      setTimeout(() => {
        img.classList.add('animate-in');
      }, index * 300);
    });

    showcaseStats.forEach((stat, index) => {
      setTimeout(
        () => {
          stat.classList.add('animate-in');
        },
        600 + index * 100,
      );
    });
  }

  animatePersonalizedElements() {
    const mainImage = document.querySelector('.personalized-main-image');
    const videoSection = document.querySelector('.personalized-video');
    const features = document.querySelectorAll('.feature-item');

    if (mainImage) {
      setTimeout(() => mainImage.classList.add('animate-in'), 0);
    }

    if (videoSection) {
      setTimeout(() => videoSection.classList.add('animate-in'), 200);
    }

    features.forEach((feature, index) => {
      setTimeout(
        () => {
          feature.classList.add('animate-in');
        },
        400 + index * 150,
      );
    });
  }

  updateUI() {
    // Update navigation dots
    this.navDots.forEach((dot, index) => {
      dot.hidden = index >= this.totalSections;
      dot.setAttribute('aria-current', index === this.currentSection ? 'true' : 'false');
      if (index === this.currentSection) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });

    // Update up/down buttons visibility
    if (this.upButton) {
      if (this.currentSection > 0) {
        this.upButton.classList.remove('d-none');
      } else {
        this.upButton.classList.add('d-none');
      }
    }

    if (this.downButton) {
      if (this.currentSection < this.totalSections - 1) {
        this.downButton.classList.remove('d-none');
      } else {
        this.downButton.classList.add('d-none');
      }
    }

    // Update progress bar
    if (this.progressBar) {
      const progress = ((this.currentSection + 1) / this.totalSections) * 100;
      this.progressBar.style.width = `${progress}%`;
    }

    // Update section counter
    if (this.sectionCounter) {
      this.sectionCounter.textContent = this.currentSection + 1;
    }

    // Update URL hash for deep linking
    const sectionIds = Array.from(this.sections, (section) => section.id.replace(/-section$/, ''));
    if (sectionIds[this.currentSection]) {
      history.replaceState(null, null, `#${sectionIds[this.currentSection]}`);
    }
  }

  // Public API
  getCurrentSection() {
    return this.currentSection;
  }

  getTotalSections() {
    return this.totalSections;
  }

  goToSection(index) {
    this.navigateToSection(index);
  }

  destroy() {
    // Remove event listeners
    this.body.removeEventListener('wheel', this.handleWheel);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('scroll', this.handleParallax);
    window.removeEventListener('resize', this.handleResize);
    if (this._onTouchMove && this.container) {
      this.container.removeEventListener('touchmove', this._onTouchMove);
    }

    // Reset styles
    document.body.style.overflow = '';
    document.documentElement.classList.remove('resta-shop-desktop-active');
    this.sections.forEach((section) => {
      section.style.position = '';
      section.style.transform = '';
      section.style.transition = '';
    });

    console.log('ScrollManager destroyed');
  }
}

// Auto-initialize if DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ScrollManager = ScrollManager;
  });
} else {
  window.ScrollManager = ScrollManager;
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ScrollManager;
}
